import { useMemo } from 'react';
import type { CatalogRole, RouteInfo } from '../../../services/iam';
import type { PermissionLookup } from '../iamLookups';
import { buildRoleFlow, findIncludedBy } from '../primitives/roleFlowTree';
import { RoleIncludeTreeCard } from './RoleIncludeTreeCard';
import { RoleOverviewCard } from './RoleOverviewCard';
import { RolePermissionsCard } from './RolePermissionsCard';
import { RoleRoutesCard } from './RoleRoutesCard';
import { routesGrantedByRole } from './roleRoutes';

interface RoleDetailPanelProps {
    role: CatalogRole;
    allRoles: readonly CatalogRole[];
    roleLookup: ReadonlyMap<string, CatalogRole>;
    permissionLookup: PermissionLookup;
    routes: readonly RouteInfo[];
    onSelectRole: (key: string) => void;
}

// Four stacked cards: Overview, Include tree, Permissions, Routes. Cards 3/4
// are keyed by role so their local fold state resets on selection change.
export function RoleDetailPanel({
    role,
    allRoles,
    roleLookup,
    permissionLookup,
    routes,
    onSelectRole
}: RoleDetailPanelProps) {
    const includedBy = useMemo(() => findIncludedBy(role.key, allRoles), [role.key, allRoles]);
    const flowRoot = useMemo(() => buildRoleFlow(role.key, roleLookup), [role.key, roleLookup]);
    const grantedRoutes = useMemo(() => routesGrantedByRole(role, routes), [role, routes]);

    return (
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide flex flex-col gap-3.5 pr-1">
            <RoleOverviewCard
                role={role}
                includedBy={includedBy}
                routeCount={grantedRoutes.length}
                onSelectRole={onSelectRole}
            />

            {flowRoot && (
                <RoleIncludeTreeCard root={flowRoot} permissionLookup={permissionLookup} />
            )}

            <RolePermissionsCard key={role.key} role={role} permissionLookup={permissionLookup} />

            <RoleRoutesCard key={role.key} role={role} grantedRoutes={grantedRoutes} />
        </div>
    );
}
